import React, { useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { SEASON_PHASE_GROUPS } from "../constants";
import { groupLeaguesBySeasonPhase } from "../helpers";
import LeaguesFloorRow from "./LeaguesFloorRow";

export default function LeaguesFloorIndex({
  leagues,
  selectedId,
  onSelect,
  pagination,
  loading,
  onPageChange,
  groupBySeason = true,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const { page = 1, totalPages = 1, total = 0, limit = 25 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + leagues.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const groups = useMemo(() => {
    if (!groupBySeason) {
      return [{ id: "all", label: null, leagues }];
    }
    const grouped = groupLeaguesBySeasonPhase(leagues);
    const labelMap = Object.fromEntries(SEASON_PHASE_GROUPS.map((g) => [g.id, g.label]));
    return grouped.map((group) => ({
      ...group,
      label: labelMap[group.id] || group.id,
    }));
  }, [leagues, groupBySeason]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const goPage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    onPageChange(nextPage);
  };

  return (
    <aside className="amf-index alg-index" aria-label="League ledger">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">League ledger</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>{leagues.length}</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
          </span>
        </div>
      </div>

      <div
        ref={listRef}
        className={`amf-index-list alg-index-list${loading ? " alg-index-list-loading" : ""}`}
      >
        {loading && leagues.length === 0 ? (
          <div className="alg-index-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading leagues…</span>
          </div>
        ) : leagues.length === 0 ? (
          <div className="amf-index-empty">No leagues match current filters.</div>
        ) : (
          groups.map((group) => (
            <section key={group.id} className="alg-index-group">
              {group.label ? (
                <p className="alg-index-group-label">{group.label}</p>
              ) : null}
              {group.leagues.map((league) => {
                const id = league._id;
                return (
                  <div key={id} ref={id === selectedId ? selectedRef : null}>
                    <LeaguesFloorRow
                      league={league}
                      selected={id === selectedId}
                      onSelect={() => onSelect(id)}
                    />
                  </div>
                );
              })}
            </section>
          ))
        )}
      </div>

      {totalPages > 1 ? (
        <div className="amf-index-foot">
          <button type="button" className="amf-index-page" disabled={!canPrev || loading} onClick={() => goPage(page - 1)}>
            <ChevronLeft size={14} />
          </button>
          <span className="amf-index-page-label">
            {page} / {totalPages}
          </span>
          <button type="button" className="amf-index-page" disabled={!canNext || loading} onClick={() => goPage(page + 1)}>
            <ChevronRight size={14} />
          </button>
        </div>
      ) : null}
    </aside>
  );
}
